import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUtility } from '../../../context/UtilityContext';
import {
  receiveCall,
  terminateCall,
} from '../../../reducers/wca/sales-support/sales-support-action';
import './CallPanel.scss'; // your styling for floating panel
import { WebRTCService } from '../webRTC';

const CallPanel = ({ isOpen, callerPhone, callData, onAccept, onDecline, onClose }) => {
  const [callState, setCallState] = useState('incoming'); // 'incoming' | 'connecting' | 'connected' | 'disconnected'
  const [isMuted, setIsMuted] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [callTimer, setCallTimer] = useState(0);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState('new');

  const dispatch = useDispatch();
  const { selectedMetadataId } = useUtility();

  const webrtcRef = useRef(null);

  // Reset state on open/close
  useEffect(() => {
    if (isOpen) {
      setCallState('incoming');
      setIsMuted(false);
      setIsMinimized(false);
      setCallTimer(0);
      setError('');
      setConnectionState('new');
    } else {
      if (webrtcRef.current) {
        webrtcRef.current.disconnect();
        webrtcRef.current = null;
      }
    }
  }, [isOpen]);

  // Monitor connection state
  useEffect(() => {
    if (callState === 'connected') {
      const interval = setInterval(() => {
        if (webrtcRef.current) {
          const state = webrtcRef.current.getConnectionState();
          setConnectionState(state || 'unknown');
          setCallTimer((prev) => prev + 1);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [callState]);

  // Accept call
  const handleAccept = async () => {
    if (!callData || !callData.message?.session?.sdp) {
      setError('Invalid call data');
      return;
    }

    try {
      setCallState('connecting');
      setError('');

      webrtcRef.current = new WebRTCService();
      await webrtcRef.current.initializePeerConnection();

      const answerSdp = await webrtcRef.current.createAnswer(callData.message.session.sdp);

      const payload = {
        callId: callData.msgId,
        sdpAnswer: answerSdp,
      };

      try {
        await dispatch(
          receiveCall({
            metadataid: selectedMetadataId,
            phone: callerPhone,
            data: payload,
          }),
        ).unwrap();
        setCallState('connected');
        onAccept(answerSdp);
      } catch (apiErr) {
        console.error('Failed to notify backend about accepted call', apiErr);
        setError('Failed to accept call: ' + (apiErr?.message || apiErr));
        setCallState('incoming');
        // ensure we close peer connection on failure
        webrtcRef.current?.disconnect();
        webrtcRef.current = null;
      }
    } catch (err) {
      console.error('Error accepting call:', err);
      setError('Failed to accept call: ' + (err.message || err));
      setCallState('incoming');
    }
  };

  // Decline call
  const handleDecline = async () => {
    if (callData) {
      try {
        const payload = { callId: callData.msgId, action: 'REJECT' };
        await dispatch(
          terminateCall({
            metadataid: selectedMetadataId,
            phone: callerPhone,
            data: payload,
          }),
        ).unwrap();
      } catch (err) {
        console.error('Error rejecting call:', err);
        setError('Failed to reject call: ' + (err?.message || err));
      }
    }

    // Cleanup local resources and update UI
    webrtcRef.current?.disconnect();
    webrtcRef.current = null;
    setCallState('disconnected');
    setIsMinimized(true);
    onDecline();
  };

  // End/Disconnect call
  const handleDisconnect = async () => {
    if (callData) {
      try {
        const payload = { callId: callData.msgId, action: 'TERMINATE' };
        await dispatch(
          terminateCall({
            metadataid: selectedMetadataId,
            phone: callerPhone,
            data: payload,
          }),
        ).unwrap();
      } catch (err) {
        console.error('Error terminating call:', err);
        setError('Failed to terminate call: ' + (err?.message || err));
      }
    }

    // Cleanup local resources
    if (webrtcRef.current) {
      webrtcRef.current.disconnect();
      webrtcRef.current = null;
    }

    webrtcRef.current?.disconnect();
    webrtcRef.current = null;
    setCallState('disconnected');
    setCallTimer(0);
    setIsMinimized(true);
    onDecline();
    onClose();
  };

  // Toggle mute
  const toggleMute = () => {
    if (!webrtcRef.current) return;
    if (isMuted) webrtcRef.current.unmuteAudio();
    else webrtcRef.current.muteAudio();
    setIsMuted(!isMuted);
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const formatTime = (sec) =>
    `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  const [isOnHold, setIsOnHold] = useState(false);
  const user = useSelector((s) => s.authenticate.user);

  const getInitials = (text) => {
    if (!text) return 'C';
    const t = String(text).trim();
    if (!isNaN(Number(t))) return t.slice(-2);
    return t
      .split(' ')
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const toggleHold = () => {
    setIsOnHold((p) => !p);
    // placeholder: integrate hold with WebRTC if available
    if (webrtcRef.current && typeof webrtcRef.current.toggleHold === 'function') {
      webrtcRef.current.toggleHold();
    }
  };

  // DRAGGABLE: position state and pointer handlers
  const [position, setPosition] = useState({ left: null, top: 86 });
  const panelRef = useRef(null);
  const headerRef = useRef(null);
  const draggingRef = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const zIndexRef = useRef(11000);

  useEffect(() => {
    // initialize position to right side if not set
    if (position.left === null && typeof window !== 'undefined') {
      const w = Math.min(380, Math.max(320, Math.floor(window.innerWidth * 0.28)));
      setPosition({ left: window.innerWidth - w - 28, top: position.top || 86 });
    }
    // clamp on resize
    const handleResize = () => {
      if (!panelRef.current) return;
      const rect = panelRef.current.getBoundingClientRect();
      const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);
      const maxTop = Math.max(8, window.innerHeight - rect.height - 8);
      setPosition((p) => ({ left: Math.min(p.left, maxLeft), top: Math.min(p.top, maxTop) }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onPointerDown = (e) => {
    if (e.button && e.button !== 0) return; // only left mouse
    if (!panelRef.current) return;
    draggingRef.current = true;
    zIndexRef.current += 1;
    panelRef.current.style.zIndex = zIndexRef.current;

    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    // capture pointer for smooth dragging
    try {
      headerRef.current?.setPointerCapture?.(e.pointerId);
    } catch (err) {}

    const onPointerMove = (ev) => {
      if (!draggingRef.current) return;
      ev.preventDefault();
      const x = ev.clientX - dragOffset.current.x;
      const y = ev.clientY - dragOffset.current.y;
      const rect = panelRef.current.getBoundingClientRect();
      const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);
      const maxTop = Math.max(8, window.innerHeight - rect.height - 8);
      const newLeft = Math.min(Math.max(8, x), maxLeft);
      const newTop = Math.min(Math.max(8, y), maxTop);
      setPosition({ left: newLeft, top: newTop });
    };

    const onPointerUp = (ev) => {
      draggingRef.current = false;
      try {
        headerRef.current?.releasePointerCapture?.(ev.pointerId);
      } catch (err) {}
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);

      // optional snap to edge if near
      const rect2 = panelRef.current.getBoundingClientRect();
      const middle = window.innerWidth / 2;
      if (rect2.left > middle) {
        // snap right
        setPosition((p) => ({ ...p, left: window.innerWidth - rect2.width - 28 }));
      } else {
        // snap left
        setPosition((p) => ({ ...p, left: 16 }));
      }
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  if (!isOpen) return null;

  return (
    <>
      {!isMinimized && (
        <div
          ref={panelRef}
          className={`call-panel ${callState === 'connected' ? 'visible' : 'entering'}`}
          style={{
            top: position.top ? `${position.top}px` : undefined,
            left: position.left ? `${position.left}px` : undefined,
            right: 'auto',
          }}
        >
          <div
            className="call-header draggable"
            ref={headerRef}
            onPointerDown={onPointerDown}
            role="button"
            tabIndex={0}
            aria-label="Drag call panel"
          >
            <div className="call-avatar">
              <div className="initials">{getInitials(callerPhone || user?.userName)}</div>
            </div>

            <div className="call-meta">
              <div className="caller-name">{callerPhone}</div>
              <div className="caller-info">
                {callState === 'incoming'
                  ? 'Incoming call'
                  : callState === 'connecting'
                  ? 'Connecting...'
                  : 'On Call'}
              </div>
              {callState === 'connected' && (
                <div className="call-timer">{formatTime(callTimer)}</div>
              )}
            </div>

            <div className="header-controls">
              <div className="small-badge">WCA</div>
              <button aria-label="Minimize" onClick={() => setIsMinimized(true)}>
                —
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="call-body">
            <div className="waveform">
              {callState === 'connected' ? '● ● ●  Live Audio' : 'Incoming call waveform'}
            </div>

            <div className="actions-wrap">
              <div className="action-card">
                <div className="call-actions-rect">
                  {callState === 'incoming' && (
                    <>
                      <button
                        className="action-rect primary"
                        onClick={async () => {
                          await handleAccept();
                        }}
                      >
                        Answer
                      </button>
                      <button className="action-rect danger" onClick={handleDecline}>
                        Decline
                      </button>
                    </>
                  )}

                  {callState === 'connecting' && <div className="center">Connecting...</div>}

                  {callState === 'connected' && (
                    <>
                      <button className="action-rect" onClick={toggleMute}>
                        {isMuted ? 'Unmute' : 'Mute'}
                      </button>
                      <button className="action-rect" onClick={toggleHold}>
                        {isOnHold ? 'Resume' : 'Hold'}
                      </button>
                      <button className="action-rect danger" onClick={handleDisconnect}>
                        End Call
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* <div className="secondary-controls">
              <button onClick={() => console.debug('Transfer')}>Transfer</button>
              <button onClick={() => console.debug('Record')}>Record</button>
              <button onClick={() => console.debug('Add Note')}>Add Note</button>
            </div> */}
          </div>

          <div className="call-status">Status: {connectionState}</div>
        </div>
      )}

      {isMinimized && (
        <div className="dynamic-island" onClick={handleRestore}>
          <div className="mini-left">
            <div className="caller">{callerPhone}</div>
            <div className="mini-status">
              {callState === 'connected' ? `On Call ${formatTime(callTimer)}` : 'Incoming'}
            </div>
          </div>
          <div className="mini-actions" onClick={(e) => e.stopPropagation()}>
            <button className="mini-btn" onClick={() => toggleMute()}>
              {isMuted ? '🔇' : '🎤'}
            </button>
            <button className="mini-btn danger" onClick={() => handleDisconnect()}>
              End
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CallPanel;
